import { useEffect } from 'react';
import { useTimeIntervalStore } from '../stores/appTimeIntervalStore';
import { useOrderHistory } from '../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../stores/appRevenueHistoryStore';
import { getCombinedDateRange } from '../utils/dateRangeFilters';
import { calculateEndDate, startOfPeriod } from '../utils/dateUtils';
import { TimeViewOption } from '../components/ui/TimeControls';


/**
 * Custom hook that handles all time control logic.
 * Components just need to call this hook and the time controls will work automatically.
 *
 * The hook:
 * - Provides current time state (view, startDate, endDate)
 * - Automatically updates date range when view changes
 * - Handles 'all' time selection by calculating full data range
 */
export function useTimeControls(dataRange?: { start: Date; end: Date } | null) {
  const view = useTimeIntervalStore(s => s.view);
  const startDate = useTimeIntervalStore(s => s.startDate);
  const endDate = useTimeIntervalStore(s => s.endDate);
  const setView = useTimeIntervalStore(s => s.setView);
  const setDateRange = useTimeIntervalStore(s => s.setDateRange);

  const orderHistoryStore = useOrderHistory();
  const revenueHistoryStore = useRevenueHistory();

  /**
   * Handle view changes (day/week/month/all)
   * Updates both the view and calculates appropriate date range
   */
  const handleViewChange = (newView: TimeViewOption) => {
    setView(newView);

    if (newView === 'all') {
      // For 'all', set date range to full data range (earliest to latest data)
      const fullRange = dataRange !== undefined ? dataRange : getCombinedDateRange(
        orderHistoryStore.orderHistoryList,
        revenueHistoryStore.revenueHistoryList
      );
      if (fullRange) {
        // Has data: cap start date to Jan 1, 2025 to prevent going too far in the past
        const minDate = new Date('2020-01-01');
        const cappedStart = fullRange.start < minDate ? minDate : fullRange.start;
        setDateRange(cappedStart, fullRange.end);
      } else {
        // No data: default to January 2025
        const defaultStart = new Date('2025-01-01');
        const defaultEnd = new Date('2025-12-31');
        setDateRange(defaultStart, defaultEnd);
      }
    } else {
      // For day/week/month, reset to current period (today, this week, this month)
      // This ensures we always start fresh when switching to these views
      const now = new Date();
      const newStartDate = startOfPeriod(now, newView);
      const newEndDate = calculateEndDate(newStartDate, newView);
      setDateRange(newStartDate, newEndDate);
    }
  };

  // When view is 'all' and data loads/changes, recalculate the date range
  // Only if the current range is still the default fallback (user hasn't picked custom dates)
  useEffect(() => {
    if (view !== 'all' || dataRange !== undefined) return;

    const isDefaultStart = startDate.getFullYear() === 2025 && startDate.getMonth() === 0 && startDate.getDate() === 1;
    const isDefaultEnd = endDate.getFullYear() === 2025 && endDate.getMonth() === 11 && endDate.getDate() === 31;
    if (!isDefaultStart || !isDefaultEnd) return;

    const fullRange = dataRange !== undefined ? dataRange : getCombinedDateRange(
      orderHistoryStore.orderHistoryList,
      revenueHistoryStore.revenueHistoryList
    );
    if (fullRange) {
      const minDate = new Date('2020-01-01');
      const cappedStart = fullRange.start < minDate ? minDate : fullRange.start;
      if (startDate.getTime() !== cappedStart.getTime() || endDate.getTime() !== fullRange.end.getTime()) {
        setDateRange(cappedStart, fullRange.end);
      }
    }
  }, [view, startDate, endDate, orderHistoryStore.orderHistoryList, revenueHistoryStore.revenueHistoryList, setDateRange, dataRange]);

  // Isolated report histories can arrive after selecting All. Only react to the
  // published bounds/view changing, so a subsequent custom range remains editable.
  useEffect(() => {
    if (view !== 'all' || !dataRange) return;
    const minDate = new Date('2020-01-01');
    const cappedStart = dataRange.start < minDate ? minDate : dataRange.start;
    const current = useTimeIntervalStore.getState();
    if (current.startDate.getTime() !== cappedStart.getTime() || current.endDate.getTime() !== dataRange.end.getTime()) {
      setDateRange(cappedStart, dataRange.end);
    }
  }, [view, dataRange, setDateRange]);

  /**
   * Handle explicit date range changes (from TimeControls component)
   */
  const handleDateRangeChange = (start: Date, end: Date) => {
    setDateRange(start, end);
  };

  return {
    // Current state
    view,
    startDate,
    endDate,
    selectedTimeOption: view as TimeViewOption,
    dateRange: { start: startDate, end: endDate },

    // Handlers
    handleViewChange,
    handleDateRangeChange,

    // Wrapper for TopSection compatibility
    setDateRange: (range: { start: Date; end: Date }) => {
      handleDateRangeChange(range.start, range.end);
    },
    setSelectedTimeOption: handleViewChange,
  };
}
